import React, { useState, useEffect, useMemo } from "react";
import {
  View,
  Text,
  Platform,
  TouchableOpacity,
  Alert,
} from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import ActionSheetIOS from "../../utils/helpers/ActionSheetIOS";
import { useSelector, useDispatch } from "react-redux";
import { getFormNames } from "../../utils/helpers/equipmentForms";
import { getFirestore, doc, collection, addDoc } from "firebase/firestore";
import { Picker } from "@react-native-picker/picker";
import { firebaseApp } from "../../firebase/config";
import { buildingListActions } from "../../store/building-slice";
import styles from "./styles";
import { EQUIPMENT_FORMS } from "../../utils/helpers/equipmentForms";
import WizardForm from "../../components/WizardForm";
import SectionCard from "../../components/SectionCard";
import SelectField from "../../components/SelectField";
import { executeFirestoreWriteWithQueue } from "../../utils/offlineWriteQueue";

const EquipmentForm = ({ route, navigation }) => {
  const [selectedForm, setSelectedForm] = useState("");
  const [selectedFormType, setSelectedFormType] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formNames = getFormNames();
  const { siteId } = route.params;
  const date = new Date();
  const dateString = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  const userUid = useSelector((state) => state.auth.uid);
  const selectedBuilding = useSelector((state) => state.buildingList.selectedBuilding);
  const siteIsAdhoc = selectedBuilding?.isAdhoc || false;
  const dispatch = useDispatch();
  const db = getFirestore(firebaseApp);
  const siteRef = doc(db, siteIsAdhoc ? "adhocSites" : "BuildingsX", siteId);

  const siteName = selectedBuilding?.name || selectedBuilding?.site_name || siteId;

  useEffect(() => {
    if (!selectedForm) {
      setSelectedFormType("");
    }
  }, [selectedForm]);

  const questions = useMemo(() => {
    if (!selectedForm || !selectedFormType) {
      return [];
    }

    const typeKey = selectedFormType.toLocaleLowerCase();
    return EQUIPMENT_FORMS[selectedForm]?.[typeKey]?.questions || [];
  }, [selectedForm, selectedFormType]);

  const openFormMenuOnIos = () => {
    if (!ActionSheetIOS) {
      return Alert.alert(
        "Unsupported",
        "Action sheets are only supported on iOS in this web build."
      );
    }

    ActionSheetIOS.showActionSheetWithOptions(
      {
        options: ["Cancel", ...formNames],
        cancelButtonIndex: 0,
        userInterfaceStyle: "dark",
      },
      (buttonIndex) => {
        if (buttonIndex === 0) {
          return setSelectedForm("");
        }
        setSelectedForm(formNames[buttonIndex - 1]);
      }
    );
  };

  const submitFormHandler = async (form) => {
    if (!selectedForm || !selectedFormType) {
      setFormError("Please select equipment and form type before submitting.");
      return;
    }

    setFormError("");
    setIsSubmitting(true);

    const documentToAdd = {
        report: "",
        date,
        machine: selectedForm,
        preUseChecks: true,
        questions: form.map(({ question, value, comments }) => ({
          question,
          value,
          comments: comments || "",
        })),
        type: "Access Equipment",
        accessFormType: selectedFormType.toLocaleLowerCase(),
        completedBy: userUid,
      };
    try {
      const { queued } = await executeFirestoreWriteWithQueue({
        db,
        queueLabel: "equipment-form",
        writeOperation: async () => {
          await addDoc(collection(siteRef, "windowCleaningForms"), documentToAdd);
        },
      });

      if (selectedFormType === "Pre") {
        dispatch(buildingListActions.setEquipmentForm(documentToAdd));
      }

      const alertMessage = queued
        ? "Offline: equipment checklist saved locally and queued for sync."
        : "Thanks — the equipment checklist has been submitted.";

      if (Platform.OS === "web") {
        alert(alertMessage);
        navigation.navigate("Building");
      } else {
        Alert.alert("Thanks", alertMessage, [
          { text: "OK", onPress: () => navigation.navigate("Building") },
        ]);
      }
    } catch (error) {
      const message = error?.message || "Unable to submit the equipment form.";
      setFormError(message);
      if (Platform.OS !== "web") {
        Alert.alert("Oops", message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <KeyboardAwareScrollView
      contentContainerStyle={styles.root}
      enableOnAndroid
      enableAutomaticScroll={Platform.OS === "ios"}
    >
      <View style={styles.pageHeader}>
        <Text style={styles.pageTitle}>Equipment Inspection</Text>
        <Text style={styles.pageSubtitle}>
          Select the equipment checklist and complete the inspection questions before starting the clean.
        </Text>
      </View>

      <SectionCard
        title="Site details"
        subtitle="Confirm the active location and inspection date."
      >
        <View style={styles.infoRow}>
          <View style={styles.infoBlock}>
            <Text style={styles.infoLabel}>Site</Text>
            <Text style={styles.infoValue}>{siteName}</Text>
          </View>
          <View style={styles.infoBlock}>
            <Text style={styles.infoLabel}>Date</Text>
            <Text style={styles.infoValue}>{dateString}</Text>
          </View>
        </View>
      </SectionCard>

      <SectionCard
        title="Choose equipment"
        subtitle="Pick the equipment and inspection type for this report."
      >
        <SelectField
          label="Equipment checklist"
          value={selectedForm}
          placeholder="Select equipment"
          options={formNames}
          onValueChange={setSelectedForm}
          helperText="You can change this at any time before submitting."
        />

        <SelectField
          label="Form type"
          value={selectedFormType}
          placeholder="Select type"
          options={["Pre", "Post"]}
          onValueChange={setSelectedFormType}
          helperText="Pre-use forms are required before cleaning begins."
          error={selectedForm && !selectedFormType ? "Form type is required." : undefined}
        />

        {Platform.OS === "ios" && (
          <TouchableOpacity style={styles.iosHintButton} onPress={openFormMenuOnIos}>
            <Text style={styles.iosHintText}>
              Tap here to choose equipment using the native iOS picker.
            </Text>
          </TouchableOpacity>
        )}
      </SectionCard>

      {!!formError && <Text style={styles.formError}>{formError}</Text>}

      {!!questions.length && (
        <SectionCard
          title={`${selectedFormType || ""} ${selectedForm} Questions`}
          subtitle="Answer each item as you move through the checklist."
        >
          <WizardForm
            questions={questions}
            submitForm={submitFormHandler}
            isSubmitting={isSubmitting}
          />
        </SectionCard>
      )}

      {selectedForm && selectedFormType && !questions.length && (
        <SectionCard title="No questions available" subtitle="There are no questions available for this selection.">
          <Text style={styles.emptyMessage}>
            Please verify the equipment and form type, or try another checklist.
          </Text>
        </SectionCard>
      )}
    </KeyboardAwareScrollView>
  );
};

export default EquipmentForm;
